import React, { useState, useEffect, useRef } from "react";
import {
  fetchPromoterRequests,
  updatePromoterRequestStatus,
} from "../../services/promoterRequestService";
import { Icon } from "@iconify/react";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";
import { formatApiDate } from "../../utils/dateHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Button } from "react-bootstrap";

const CLUB_COMMISSION_ROWS = [
  {
    key: "overallVenue",
    label: "Venue",
    typeFixed: "flatfee",
    required: false, // optional — top-level commissionType/commissionValue
    inServices: false,
  },
  {
    key: "Scanned",
    label: "Scanned",
    typeFixed: "flatfee",
    required: true,
    inServices: true,
  },
  {
    key: "Booth",
    label: "Booth",
    typeFixed: null,
    required: true,
    inServices: true,
  },
  {
    key: "Table",
    label: "Table",
    typeFixed: null,
    required: true,
    inServices: true,
  },
  {
    key: "VIP Entry",
    label: "VIP Entry",
    typeFixed: null,
    required: true,
    inServices: true,
  },
  {
    key: "BottleService",
    label: "Bottle Service",
    typeFixed: null,
    required: true,
    inServices: true,
  },
];

const normalizeServiceKey = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "")
    .replace(/entery/g, "entry");

const SERVICE_KEY_MATCHERS = CLUB_COMMISSION_ROWS.reduce(
  (acc, row) => {
    acc[normalizeServiceKey(row.key)] = row.key;
    return acc;
  },
  {
    // Aliases for older / alternate backend keys
    [normalizeServiceKey("overallVenue")]: "overallVenue",
    [normalizeServiceKey("Overall_Club_Commission")]: "overallVenue",
    [normalizeServiceKey("OverallClubCommission")]: "overallVenue",
    [normalizeServiceKey("overall")]: "overallVenue",
    [normalizeServiceKey("ClubVenue")]: "overallVenue",
    [normalizeServiceKey("Club Venue")]: "overallVenue",
    [normalizeServiceKey("VIP_Entery")]: "VIP Entry",
    [normalizeServiceKey("VIP_Entry")]: "VIP Entry",
    [normalizeServiceKey("VIP Entry")]: "VIP Entry",
    [normalizeServiceKey("Bottle_Service")]: "BottleService",
    [normalizeServiceKey("Bottle Service")]: "BottleService",
    [normalizeServiceKey("BottleService")]: "BottleService",
  },
);

const resolveServiceRowKey = (rawType) =>
  SERVICE_KEY_MATCHERS[normalizeServiceKey(rawType)] || null;

const createEmptyClubCommissions = () =>
  CLUB_COMMISSION_ROWS.reduce((acc, row) => {
    acc[row.key] = {
      commissionType: row.typeFixed || "flatfee",
      commissionValue: "",
    };
    return acc;
  }, {});

const VenuePromotionRequestListLayer = ({ requestType = "host" }) => {
  const [items, setItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [commissionTypeFilter, setCommissionTypeFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [modalMode, setModalMode] = useState("approve"); // approve | edit
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [commissionType, setCommissionType] = useState("percentage");
  const [commissionValue, setCommissionValue] = useState("");
  const [clubCommissions, setClubCommissions] = useState(
    createEmptyClubCommissions,
  );
  const [commissionErrors, setCommissionErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Keep last saved club commissions so Edit can prefill even if list API omits services
  const commissionCacheRef = useRef({});

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("venuePromotionCommissionCache");
      if (raw) {
        commissionCacheRef.current = {
          ...commissionCacheRef.current,
          ...JSON.parse(raw),
        };
      }
    } catch (e) {
      // ignore cache parse errors
    }
  }, []);

  const persistCommissionCache = (id, data) => {
    commissionCacheRef.current[id] = data;
    try {
      sessionStorage.setItem(
        "venuePromotionCommissionCache",
        JSON.stringify(commissionCacheRef.current),
      );
    } catch (e) {
      // ignore quota / private mode errors
    }
  };

  const isCommissionSort = sortColumn === "commissionValue";
  const showEventName = requestType === "event";
  const tableColSpan = showEventName ? 8 : 7;
  // Venue (host/club) uses multi-row commission table; Event keeps simple fields
  const isClubCommissionModal = requestType !== "event";

  const getCommissionType = (item) => {
    const raw = item?.commissionType ?? item?.commission_type ?? "";
    const normalized = String(raw)
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    if (normalized === "flatfee" || normalized === "flat") return "flatfee";
    if (normalized === "percentage" || normalized === "percent")
      return "percentage";
    return "percentage";
  };

  // Request type: API uses host/event; UI labels are Club/Event
  const getRequestType = (item) => {
    const raw =
      item?.type ??
      item?.requestType ??
      item?.venueType ??
      item?.listingType ??
      "";
    const normalized = String(raw)
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    if (normalized === "host" || normalized === "club") return "host";
    if (normalized === "event") return "event";
    return "";
  };

  const getRequestTypeLabel = (item) => {
    const type = getRequestType(item);
    if (type === "host") return "Club";
    if (type === "event") return "Event";
    return "N/A";
  };

  const getHostName = (item) => {
    const name =
      item?.hostName ||
      item?.host?.fullName ||
      item?.host?.name ||
      item?.clubName ||
      item?.userId?.fullName ||
      "";
    return name || "-";
  };

  const getEventName = (item) => {
    const name = item?.eventName || item?.event?.name || "";
    return name || "-";
  };

  const getCommissionValue = (item) => {
    const raw =
      item?.commissionValue ??
      item?.commission_value ??
      item?.value ??
      item?.commission?.commissionValue ??
      item?.commission?.value ??
      item?.overallVenue?.commissionValue ??
      null;
    if (raw == null || raw === "") return "";
    return String(raw);
  };

  const normalizeCommissionTypeValue = (raw, fallback = "flatfee") => {
    const normalized = String(raw || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "");
    if (normalized === "flatfee" || normalized === "flat") return "flatfee";
    if (normalized === "percentage" || normalized === "percent")
      return "percentage";
    return fallback;
  };

  const getEntryCommissionValue = (entry) => {
    if (entry == null || typeof entry !== "object") {
      if (entry == null || entry === "") return "";
      return String(entry);
    }
    const raw =
      entry.commissionValue ??
      entry.commission_value ??
      entry.value ??
      entry.amount ??
      entry.commissionAmount ??
      "";
    if (raw == null || raw === "") return "";
    return String(raw);
  };

  const getClubCommissionSource = (item) => {
    if (!item || typeof item !== "object") return null;
    const candidates = [
      item.services,
      item.commissionDetails,
      item.commissions,
      item.clubCommissions,
      item.serviceCommissions,
      item.serviceList,
      item.commission?.services,
      item.commissionData?.services,
      item.commission?.details,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.length > 0) return candidate;
      if (
        candidate &&
        typeof candidate === "object" &&
        !Array.isArray(candidate) &&
        Object.keys(candidate).length > 0
      ) {
        return candidate;
      }
    }
    return null;
  };

  const normalizeServicesToArray = (source) => {
    if (Array.isArray(source)) return source;
    if (source && typeof source === "object") {
      return Object.entries(source).map(([key, val]) => ({
        servicesType: key,
        commissionType:
          val?.commissionType ?? val?.type ?? val?.commission_type,
        commissionValue:
          val?.commissionValue ??
          val?.commission_value ??
          val?.value ??
          val?.amount,
      }));
    }
    return [];
  };

  const findServiceEntry = (source, rowKey) => {
    if (!source) return null;

    if (Array.isArray(source)) {
      return (
        source.find((entry) => {
          const rawType =
            entry?.servicesType ??
            entry?.serviceType ??
            entry?.service ??
            entry?.type ??
            entry?.key ??
            entry?.name ??
            entry?.label ??
            "";
          return resolveServiceRowKey(rawType) === rowKey;
        }) || null
      );
    }

    // Object map: try exact key, then normalized keys
    if (source[rowKey] != null) return source[rowKey];
    const matchedKey = Object.keys(source).find(
      (key) => resolveServiceRowKey(key) === rowKey,
    );
    return matchedKey ? source[matchedKey] : null;
  };

  const buildClubCommissionsFromItem = (item) => {
    const base = createEmptyClubCommissions();
    const cached = item?._id ? commissionCacheRef.current[item._id] : null;
    const mergedItem = {
      ...item,
      commissionType:
        item?.commissionType ?? item?.commission_type ?? cached?.commissionType,
      commissionValue:
        item?.commissionValue ??
        item?.commission_value ??
        item?.value ??
        cached?.commissionValue,
      services:
        Array.isArray(item?.services) && item.services.length > 0
          ? item.services
          : Array.isArray(cached?.services) && cached.services.length > 0
            ? cached.services
            : item?.services,
    };

    const source = getClubCommissionSource(mergedItem);
    const cachedSource = Array.isArray(cached?.services)
      ? cached.services
      : null;

    CLUB_COMMISSION_ROWS.forEach((row) => {
      const fromSource = findServiceEntry(source, row.key);
      const fromCache = findServiceEntry(cachedSource, row.key);
      const picked = fromSource || fromCache;
      if (!picked) return;

      const value = getEntryCommissionValue(picked);
      base[row.key] = {
        commissionType: row.typeFixed
          ? row.typeFixed
          : normalizeCommissionTypeValue(
              picked.commissionType ?? picked.type ?? picked.commission_type,
              "flatfee",
            ),
        commissionValue: value,
      };
    });

    // ClubVenue / overall from top-level fields when services don't provide it
    const topLevelStr = getCommissionValue(mergedItem);
    const existingOverall = getEntryCommissionValue(base.overallVenue);
    if (!existingOverall && topLevelStr !== "") {
      base.overallVenue = {
        commissionType: "flatfee",
        commissionValue: topLevelStr,
      };
    } else if (!existingOverall && topLevelStr === "") {
      // keep empty overallVenue from createEmptyClubCommissions
      base.overallVenue = {
        commissionType: "flatfee",
        commissionValue: "",
      };
    }

    return base;
  };

  const formatCommissionCell = (item) => {
    if (isClubCommissionModal) {
      const cached = item?._id ? commissionCacheRef.current[item._id] : null;
      const services =
        (Array.isArray(item?.services) && item.services.length > 0
          ? item.services
          : null) ||
        (Array.isArray(cached?.services) && cached.services.length > 0
          ? cached.services
          : null);
      if (services?.length) {
        return `${services.length} service(s)`;
      }
      const value = getCommissionValue(item);
      if (value !== "" && value !== "0") {
        return `Venue: Flat Fee ${value}`;
      }
      return "N/A";
    }

    if (getCommissionValue(item) === "") return "N/A";
    return `${
      getCommissionType(item) === "flatfee" ? "Flat Fee" : "Percentage"
    }: ${getCommissionValue(item)}${
      getCommissionType(item) === "flatfee" ? "" : "%"
    }`;
  };

  const getNumericCommissionValue = (item) => {
    const raw = getCommissionValue(item);
    if (raw === "") return null;
    // Strip %, currency, etc. so "8%" / "9.99" both parse
    const cleaned = String(raw).replace(/[^0-9.-]/g, "");
    if (cleaned === "" || cleaned === "-" || cleaned === ".") return null;
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const sortByCommissionValue = (list, order) => {
    return [...list].sort((a, b) => {
      const aVal = getNumericCommissionValue(a);
      const bVal = getNumericCommissionValue(b);
      const aIsNA = aVal == null;
      const bIsNA = bVal == null;

      // ASC: numbers first (low→high), N/A last
      // DESC: N/A first, then numbers (high→low)
      if (aIsNA && bIsNA) return 0;
      if (aIsNA) return order === "asc" ? 1 : -1;
      if (bIsNA) return order === "asc" ? -1 : 1;

      return order === "asc" ? aVal - bVal : bVal - aVal;
    });
  };

  const mergeCommissionPreserve = (incoming, prev) =>
    incoming.map((row) => {
      const existing = prev.find((p) => p._id === row._id);
      const cached = commissionCacheRef.current[row._id];
      const apiValue =
        row?.commissionValue ?? row?.commission_value ?? row?.value;
      const apiServices = getClubCommissionSource(row);

      let next = { ...row };

      // Prefer API services; else keep previous/cached
      const apiServicesList = normalizeServicesToArray(apiServices);
      if (apiServicesList.length > 0) {
        next.services = apiServicesList;
        persistCommissionCache(row._id, {
          commissionType: next.commissionType ?? cached?.commissionType,
          commissionValue:
            next.commissionValue ?? apiValue ?? cached?.commissionValue ?? "",
          services: apiServicesList,
        });
      } else if (
        Array.isArray(existing?.services) &&
        existing.services.length > 0
      ) {
        next.services = existing.services;
      } else if (
        Array.isArray(cached?.services) &&
        cached.services.length > 0
      ) {
        next.services = cached.services;
      }

      if (
        (apiValue == null || apiValue === "") &&
        existing?.commissionValue != null &&
        existing.commissionValue !== ""
      ) {
        next = {
          ...next,
          commissionType: existing.commissionType,
          commissionValue: existing.commissionValue,
        };
      } else if (
        (apiValue == null || apiValue === "") &&
        cached?.commissionValue != null &&
        cached.commissionValue !== ""
      ) {
        next = {
          ...next,
          commissionType: cached.commissionType || "flatfee",
          commissionValue: cached.commissionValue,
        };
      }

      return next;
    });

  const fetchData = async () => {
    try {
      setLoading(true);

      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      if (
        formattedStartDate &&
        formattedEndDate &&
        moment(formattedStartDate).isAfter(moment(formattedEndDate))
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      // Commission value: numeric sort on frontend (backend sort is unreliable)
      if (isCommissionSort) {
        const res = await fetchPromoterRequests({
          page: 1,
          limit: 99999,
          search: searchQuery,
          sortBy: "createdAt",
          sortOrder: "desc",
          status: statusFilter,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          commissionType: commissionTypeFilter,
          type: requestType,
        });

        setItems((prev) => {
          const incoming = mergeCommissionPreserve(res.data?.data || [], prev);
          const sorted = sortByCommissionValue(incoming, sortOrder);
          setTotalItems(sorted.length);
          setTotalPages(Math.max(1, Math.ceil(sorted.length / itemsPerPage)));
          const start = (currentPage - 1) * itemsPerPage;
          return sorted.slice(start, start + itemsPerPage);
        });
        return;
      }

      const res = await fetchPromoterRequests({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder,
        status: statusFilter,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        commissionType: commissionTypeFilter,
        type: requestType,
      });

      setItems((prev) => mergeCommissionPreserve(res.data?.data || [], prev));
      setTotalItems(res.data?.pagination?.total || 0);
      setTotalPages(res.data?.pagination?.totalPages || 1);
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || "Failed to fetch promotion requests",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
    commissionTypeFilter,
    requestType,
    startDate,
    endDate,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      // First click on commission → ASC (low to high); other cols → DESC
      setSortOrder(column === "commissionValue" ? "asc" : "desc");
    }
    setCurrentPage(1);
  };

  const hasCommissionValue = (item) => {
    if (isClubCommissionModal) {
      if (Array.isArray(item?.services) && item.services.length > 0)
        return true;
      const source = getClubCommissionSource(item);
      if (source) {
        const hasAny = CLUB_COMMISSION_ROWS.filter((r) => r.inServices).some(
          (row) => {
            const entry = findServiceEntry(source, row.key);
            const value = entry?.commissionValue ?? entry?.value;
            return value != null && value !== "";
          },
        );
        if (hasAny) return true;
      }
    }
    return getCommissionValue(item) !== "";
  };

  const openCommissionModal = (item, mode = "approve") => {
    setSelectedRequest(item);
    setModalMode(mode);
    setCommissionErrors({});
    if (requestType !== "event") {
      const cached = item?._id ? commissionCacheRef.current[item._id] : null;
      const itemForPrefill = {
        ...item,
        commissionType:
          item?.commissionType ??
          item?.commission_type ??
          cached?.commissionType,
        commissionValue:
          item?.commissionValue ??
          item?.commission_value ??
          item?.value ??
          cached?.commissionValue,
        services:
          Array.isArray(item?.services) && item.services.length > 0
            ? item.services
            : Array.isArray(cached?.services) && cached.services.length > 0
              ? cached.services
              : item?.services,
        commissionDetails: item?.commissionDetails || cached?.commissionDetails,
        commissions: item?.commissions || cached?.commissions,
      };
      const prefilled = buildClubCommissionsFromItem(itemForPrefill);
      setClubCommissions(prefilled);
    } else {
      setCommissionType(getCommissionType(item));
      setCommissionValue(getCommissionValue(item));
    }
    setShowCommissionModal(true);
  };

  const closeCommissionModal = () => {
    setShowCommissionModal(false);
    setSelectedRequest(null);
    setCommissionType("percentage");
    setCommissionValue("");
    setClubCommissions(createEmptyClubCommissions());
    setCommissionErrors({});
    setSubmitting(false);
  };

  const sanitizeCommissionInput = (value, type) => {
    const cleaned = String(value).replace(/[^0-9.]/g, "");
    if (!cleaned) return "";
    const dotIndex = cleaned.indexOf(".");
    const hasDot = dotIndex !== -1;
    const integerPart = hasDot ? cleaned.slice(0, dotIndex) : cleaned;
    const decimalPart = hasDot
      ? cleaned.slice(dotIndex + 1).replace(/\./g, "").slice(0, 2)
      : "";
    const maxIntDigits = type === "percentage" ? 3 : 10;
    if (integerPart.length > maxIntDigits) return null;
    return hasDot ? `${integerPart}.${decimalPart}` : integerPart;
  };

  const handleCommissionValueChange = (value) => {
    const normalized = sanitizeCommissionInput(value, commissionType);
    if (normalized == null) return;
    setCommissionValue(normalized);
    setCommissionErrors((prev) => ({
      ...prev,
      commissionValue: "",
    }));
  };

  const getClubRowEntry = (key, source = clubCommissions) => {
    const row = CLUB_COMMISSION_ROWS.find((r) => r.key === key);
    const raw =
      (source && source[key] != null ? source[key] : null) ??
      (key === "VIP Entry"
        ? (source?.VIP_Entery ?? source?.VIP_Entry ?? null)
        : null) ??
      {};
    return {
      commissionType:
        row?.typeFixed ||
        normalizeCommissionTypeValue(raw.commissionType, "flatfee"),
      commissionValue:
        raw.commissionValue == null || raw.commissionValue === ""
          ? ""
          : String(raw.commissionValue),
    };
  };

  const handleClubCommissionTypeChange = (key, type) => {
    setClubCommissions((prev) => {
      const current = getClubRowEntry(key, prev);
      return {
        ...prev,
        [key]: {
          commissionType: type || "flatfee",
          commissionValue: current.commissionValue || "",
        },
      };
    });
    setCommissionErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleClubCommissionValueChange = (key, value) => {
    const current = getClubRowEntry(key);
    const type = current.commissionType || "flatfee";
    const normalized = sanitizeCommissionInput(value, type);
    if (normalized == null) return;
    setClubCommissions((prev) => {
      const prevEntry = getClubRowEntry(key, prev);
      return {
        ...prev,
        [key]: {
          commissionType: prevEntry.commissionType || "flatfee",
          commissionValue: normalized,
        },
      };
    });
    setCommissionErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const validateSingleCommission = (type, value, { optional = false } = {}) => {
    const resolvedType = type || "flatfee";
    if (optional && (value === "" || value == null)) return "";
    if (!resolvedType) return "Commission type is required";
    if (value === "" || value == null || Number.isNaN(Number(value))) {
      return "Commission value is required";
    }
    const num = Number(value);
    if (optional && num === 0) return "";
    if (num <= 0) return "Commission value must be greater than zero";
    const decimalPart = String(value).split(".")[1];
    if (decimalPart && decimalPart.length > 2) {
      return "Maximum 2 digits after decimal are allowed";
    }
    if (resolvedType === "percentage") {
      if (num < 1) return "Percentage must be at least 1";
      if (num > 100) return "Percentage cannot be more than 100";
    } else if (resolvedType === "flatfee") {
      const integerPart = String(value).split(".")[0] || "";
      if (integerPart.length > 10) {
        return "Flat fee cannot exceed 10 digits";
      }
    }
    return "";
  };

  const validateCommission = () => {
    if (isClubCommissionModal) {
      const errors = {};
      CLUB_COMMISSION_ROWS.forEach((row) => {
        const entry = getClubRowEntry(row.key);
        const type = row.typeFixed || entry.commissionType || "flatfee";
        const message = validateSingleCommission(type, entry.commissionValue, {
          optional: !row.required,
        });
        if (message) errors[row.key] = message;
      });
      setCommissionErrors(errors);
      return Object.keys(errors).length === 0;
    }

    const errors = {};
    const message = validateSingleCommission(commissionType, commissionValue);
    if (!commissionType) errors.commissionType = "Commission type is required";
    if (message) errors.commissionValue = message;
    setCommissionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCommissionSubmit = async () => {
    if (!selectedRequest?._id || !validateCommission()) return;

    setSubmitting(true);
    try {
      let payload = { status: "approved" };
      let rowUpdate = { status: "approved" };

      if (isClubCommissionModal) {
        const overall = getClubRowEntry("overallVenue");
        const overallValue =
          overall.commissionValue === "" || overall.commissionValue == null
            ? "0"
            : String(overall.commissionValue);

        const services = CLUB_COMMISSION_ROWS.filter(
          (row) => row.inServices,
        ).map((row) => {
          const entry = getClubRowEntry(row.key);
          return {
            servicesType: row.key,
            commissionType: row.typeFixed || entry.commissionType || "flatfee",
            commissionValue: String(entry.commissionValue),
          };
        });

        payload = {
          status: "approved",
          commissionType: "flatfee",
          commissionValue: overallValue,
          services,
        };
        rowUpdate = {
          status: "approved",
          commissionType: "flatfee",
          commissionValue: overallValue,
          services,
        };
      } else {
        payload = {
          ...payload,
          commissionType,
          commissionValue: String(commissionValue),
        };
        rowUpdate = {
          ...rowUpdate,
          commissionType,
          commissionValue: String(commissionValue),
        };
      }

      const response = await updatePromoterRequestStatus(
        selectedRequest._id,
        payload,
      );

      // Prefer services returned by API; otherwise keep what we just saved
      const responseData =
        response?.data?.data ||
        response?.data?.result ||
        response?.data?.request ||
        response?.data ||
        {};
      const responseServices = normalizeServicesToArray(
        getClubCommissionSource(responseData),
      );
      if (responseServices.length > 0) {
        rowUpdate.services = responseServices;
      }

      if (isClubCommissionModal || rowUpdate.services) {
        persistCommissionCache(selectedRequest._id, {
          commissionType: rowUpdate.commissionType,
          commissionValue: rowUpdate.commissionValue,
          services: rowUpdate.services || [],
        });
      }

      setItems((prev) =>
        prev.map((row) =>
          row._id === selectedRequest._id ? { ...row, ...rowUpdate } : row,
        ),
      );

      setSuccessMessage(
        response?.data?.message ||
          (modalMode === "edit"
            ? "Commission updated successfully"
            : "Request approved successfully"),
      );
      closeCommissionModal();
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save commission",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (id) => {
    const result = await showConfirmationDialog(
      "This action will reject the request.",
      "Yes, reject it!",
    );
    if (!result.isConfirmed) return;

    try {
      const response = await updatePromoterRequestStatus(id, {
        status: "rejected",
      });
      setSuccessMessage(
        response?.data?.message || "Request rejected successfully",
      );
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to reject request",
      );
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const formatDate = (dateStr) => formatApiDate(dateStr);

  const getStatusClass = (status) => {
    if (status === "approved") {
      return "bg-success-focus text-success-600 border border-success-main";
    }
    if (status === "rejected") {
      return "bg-danger-focus text-danger-600 border border-danger-main";
    }
    return "bg-warning-focus text-warning-600 border border-warning-main";
  };

  const getPaginationRange = () => {
    const maxVisiblePages = 5;
    const pages = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      for (let i = startPage; i <= endPage; i++) pages.push(i);
    }

    return pages;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setEndDate(null);
                setCurrentPage(1);
              }}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              minDate={startDate}
              preventOpenOnFocus={false}
              onKeyDown={(e) => e.preventDefault()}
              className="form-control no-caret"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              isClearable
            />
          </div>

          <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          {/* <select
            className="form-select form-select-sm w-auto ps-12"
            style={{
              height: "40px",
              minHeight: "40px",
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={commissionTypeFilter}
            onChange={(e) => {
              setCommissionTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Commission Type</option>
            <option value="flatfee">Flat Fee</option>
            <option value="percentage">Percentage</option>
          </select> */}
        </div>
      </div>

      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("host.fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Host Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "host.fullName" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "host.fullName" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                {showEventName && (
                  <th
                    onClick={() => handleSort("event.name")}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      Event Name
                      <span className="sort-colume">
                        <Icon
                          icon="mdi:arrow-up"
                          className={`arrow ${
                            sortColumn === "event.name" && sortOrder === "asc"
                              ? "active"
                              : ""
                          }`}
                        />
                        <Icon
                          icon="mdi:arrow-down"
                          className={`arrow ${
                            sortColumn === "event.name" && sortOrder === "desc"
                              ? "active"
                              : ""
                          }`}
                        />
                      </span>
                    </div>
                  </th>
                )}
                <th
                  onClick={() => handleSort("promoter.fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Promoter Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "promoter.fullName" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "promoter.fullName" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("type")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Type
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "type" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "type" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Request Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                {/* <th
                  onClick={() => handleSort("commissionValue")}
                  style={{ cursor: "pointer" }}
                > */}
                  {/* <div className="d-flex align-items-center gap-2">
                    Commission
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "commissionValue" &&
                          sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "commissionValue" &&
                          sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div> */}
                {/* </th> */}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={tableColSpan} className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>{getHostName(item)}</td>
                    {showEventName && <td>{getEventName(item)}</td>}
                    <td>{item.promoterName || "N/A"}</td>
                    <td>{getRequestTypeLabel(item)}</td>
                    <td>
                      <span
                        className={`${getStatusClass(
                          item.status,
                        )} px-24 py-4 radius-4 fw-medium text-sm`}
                      >
                        {item.status
                          ? item.status.charAt(0).toUpperCase() +
                            item.status.slice(1)
                          : "N/A"}
                      </span>
                    </td>
                    <td>{formatDate(item.requestDate)}</td>
                    {/* <td>{formatCommissionCell(item)}</td> */}
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {item.status === "pending" && (
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              title="Approve"
                              onClick={() =>
                                openCommissionModal(item, "approve")
                              }
                              className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="mdi:check" className="icon text-xl" />
                            </button>

                            <button
                              type="button"
                              title="Reject"
                              onClick={() => handleReject(item._id)}
                              className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="mdi:close" className="icon text-xl" />
                            </button>
                          </div>
                        )}
                        {/* {item.status === "pending" && (
                          <button
                            type="button"
                            title="Reject"
                            onClick={() => handleReject(item._id)}
                            className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="mdi:close" className="icon text-xl" />
                          </button>
                        )} */}
                        {item.status === "approved" && (
                           <div className="d-flex gap-2">
                                                      <button
                            type="button"
                            title="Edit Commission"
                            onClick={() => openCommissionModal(item, "edit")}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          <button
                              type="button"
                              title="Reject"
                              onClick={() => handleReject(item._id)}
                              className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="mdi:close" className="icon text-xl" />
                            </button>
                            </div>

                        )}
                        {item.status === "rejected" && (
                          <button
                              type="button"
                              title="Approve"
                              onClick={() =>
                                openCommissionModal(item, "approve")
                              }
                              className="bg-success-focus bg-hover-success-200 text-success-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            >
                              <Icon icon="mdi:check" className="icon text-xl" />
                            </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableColSpan} className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() =>
                  currentPage > 1 && setCurrentPage((prev) => prev - 1)
                }
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>
            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() =>
                  currentPage < totalPages && setCurrentPage((prev) => prev + 1)
                }
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>

      <Modal
        key={`commission-modal-${selectedRequest?._id || "new"}-${modalMode}`}
        show={showCommissionModal}
        onHide={closeCommissionModal}
        centered
        dialogClassName={
          isClubCommissionModal ? "venue-commission-modal" : undefined
        }
      >
        <Modal.Header closeButton className="py-2 px-3">
          <Modal.Title style={{ fontSize: 18 }}>
            {modalMode === "edit" ? "Edit Commission" : "Approve Request"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-2 px-3">
          {isClubCommissionModal ? (
            <>
              <style>{`
                .venue-commission-modal {
                  max-width: 680px !important;
                  width: 96% !important;
                  margin: 0.5rem auto;
                }
                .venue-commission-modal .modal-content {
                  max-height: 82vh;
                  overflow: hidden;
                }
                .venue-commission-modal .modal-body {
                  overflow-y: auto;
                  overflow-x: hidden;
                  max-height: calc(82vh - 120px);
                }
                .venue-commission-modal .commission-table-wrap {
                  width: 100%;
                  overflow-x: hidden;
                }
                .venue-commission-modal .table {
                  table-layout: fixed;
                  width: 100%;
                  margin-bottom: 0;
                }
                .venue-commission-modal .table > :not(caption) > * > * {
                  padding: 0.4rem 0.4rem;
                  vertical-align: middle;
                }
                .venue-commission-modal .col-service {
                  width: 36%;
                }
                .venue-commission-modal .col-type {
                  width: 32%;
                }
                .venue-commission-modal .col-value {
                  width: 32%;
                }
                .venue-commission-modal .form-control,
                .venue-commission-modal .form-select {
                  width: 100%;
                  max-width: 100%;
                  min-width: 0;
                  box-sizing: border-box;
                }
              `}</style>
              <div className="commission-table-wrap">
                <table className="table bordered-table sm-table mb-0 align-middle">
                  <thead>
                    <tr>
                      <th className="col-service">Service</th>
                      <th className="col-type">Commission Type</th>
                      <th className="col-value">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CLUB_COMMISSION_ROWS.map((row) => {
                      const entry = getClubRowEntry(row.key);
                      const type =
                        row.typeFixed || entry.commissionType || "flatfee";
                      const isFixedType = Boolean(row.typeFixed);
                      return (
                        <tr key={row.key}>
                          <td className="fw-medium col-service">
                            {row.label}
                            {!row.required && (
                              <span className="text-secondary-light fw-normal">
                                {" "}
                                (Optional)
                              </span>
                            )}
                            {row.required && (
                              <span className="text-danger-600">*</span>
                            )}
                            <span className="text-secondary-light fw-normal">
                              {" "}
                              {type === "percentage" ? "(%)" : "($)"}
                            </span>
                          </td>
                          <td className="col-type">
                            {isFixedType ? (
                              <input
                                type="text"
                                className="form-control"
                                value="Flat Fee"
                                disabled
                                readOnly
                              />
                            ) : (
                              <select
                                className={`form-select ${
                                  commissionErrors[row.key] ? "is-invalid" : ""
                                }`}
                                value={type}
                                onChange={(e) =>
                                  handleClubCommissionTypeChange(
                                    row.key,
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="flatfee">Flat Fee</option>
                                <option value="percentage">Percentage</option>
                              </select>
                            )}
                          </td>
                          <td className="col-value">
                            <input
                              type="text"
                              className={`form-control ${
                                commissionErrors[row.key] ? "is-invalid" : ""
                              }`}
                              title={commissionErrors[row.key] || ""}
                              placeholder={
                                type === "percentage"
                                  ? "Enter percentage"
                                  : "Enter flat fee"
                              }
                              value={entry.commissionValue}
                              onChange={(e) =>
                                handleClubCommissionValueChange(
                                  row.key,
                                  e.target.value,
                                )
                              }
                            />
                            {commissionErrors[row.key] && (
                              <div
                                className="text-danger"
                                style={{ fontSize: 11, lineHeight: 1.2 }}
                              >
                                {commissionErrors[row.key]}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Commission Type<span className="text-danger-600">*</span>
                </label>
                <select
                  className={`form-select ${
                    commissionErrors.commissionType ? "is-invalid" : ""
                  }`}
                  value={commissionType}
                  onChange={(e) => {
                    setCommissionType(e.target.value);
                    setCommissionErrors((prev) => ({
                      ...prev,
                      commissionType: "",
                    }));
                  }}
                >
                  <option value="percentage">Percentage</option>
                  <option value="flatfee">Flat Fee</option>
                </select>
                {commissionErrors.commissionType && (
                  <div className="text-danger mt-1">
                    {commissionErrors.commissionType}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Commission Value{" "}
                  {commissionType === "percentage" ? "(%)" : "($)"}
                  <span className="text-danger-600">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    commissionErrors.commissionValue ? "is-invalid" : ""
                  }`}
                  placeholder={
                    commissionType === "percentage"
                      ? "Enter percentage"
                      : "Enter flat fee"
                  }
                  value={commissionValue}
                  onChange={(e) => handleCommissionValueChange(e.target.value)}
                />
                {commissionErrors.commissionValue && (
                  <div className="text-danger mt-1">
                    {commissionErrors.commissionValue}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="py-2 px-3">
          <Button variant="secondary" onClick={closeCommissionModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCommissionSubmit}
            disabled={submitting}
          >
            {submitting
              ? "Saving..."
              : modalMode === "edit"
                ? "Update"
                : "Approve"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VenuePromotionRequestListLayer;
